import React, { useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Upload, Loader2, X } from "lucide-react";

/**
 * Reusable file upload. Uploads to /api/upload and returns storage path + url via onUploaded.
 * kind: "image" for cover/banner/avatar/media (public), "product" for downloadable asset (buyer-only)
 */
export default function FileUpload({
  kind = "image",
  accept = "image/*",
  onUploaded,
  buttonLabel = "Upload",
  currentUrl = "",
  currentName = "",
  className = "",
  testid = "file-upload",
}) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(currentUrl);

  const pick = () => inputRef.current?.click();

  const clear = () => {
    setPreview("");
    onUploaded?.(null);
  };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const form = new FormData();
    form.append("file", file);
    form.append("kind", kind);
    setUploading(true);
    try {
      const { data } = await api.post("/pluse/upload", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (kind === "image") {
        // Show public URL through backend
        const backend = process.env.REACT_APP_BACKEND_URL;
        const abs = data.url.startsWith("http") ? data.url : `${backend}${data.url}`;
        setPreview(abs);
        onUploaded?.({ ...data, url: abs });
      } else {
        setPreview(data.filename);
        onUploaded?.(data);
      }
      toast.success("Uploaded");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div className={className}>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        onChange={upload}
        className="hidden"
        data-testid={`${testid}-input`}
      />
      {kind === "image" && preview ? (
        <div className="relative rounded-2xl overflow-hidden border border-white/10 bg-white/5" data-testid={`${testid}-preview`}>
          <img src={preview} alt="" className="w-full h-40 object-cover" />
          <button
            type="button"
            onClick={clear}
            className="absolute top-2 right-2 w-8 h-8 rounded-full bg-black/70 border border-white/20 flex items-center justify-center hover:bg-black"
            data-testid={`${testid}-clear`}
          >
            <X className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={pick}
            disabled={uploading}
            className="absolute bottom-2 right-2 px-3 py-1.5 rounded-full glass-strong text-xs"
            data-testid={`${testid}-replace`}
          >
            {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Replace"}
          </button>
        </div>
      ) : preview && kind !== "image" ? (
        <div className="flex items-center justify-between p-3 rounded-2xl border border-white/10 bg-white/5" data-testid={`${testid}-file`}>
          <div className="text-sm truncate mr-2">{preview || currentName}</div>
          <div className="flex gap-2">
            <button type="button" onClick={pick} disabled={uploading} className="text-xs text-white/70 hover:text-white">
              {uploading ? "Uploading…" : "Replace"}
            </button>
            <button type="button" onClick={clear} className="text-xs text-red-400 hover:text-red-300">Remove</button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={pick}
          disabled={uploading}
          data-testid={`${testid}-btn`}
          className="w-full h-11 rounded-full border border-dashed border-white/20 hover:border-white/40 flex items-center justify-center gap-2 text-sm text-white/70 hover:text-white bg-white/5"
        >
          {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
          {uploading ? "Uploading…" : buttonLabel}
        </button>
      )}
    </div>
  );
}
